import React, { useState, useEffect, useMemo, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Edit, Trash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { t, useLanguage } from '../../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

export default function OrderList() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { lang } = useLanguage();

  useEffect(() => {
    fetch(`${apiUrl}/orders`)
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch orders');
        }
        return res.json();
      })
      .then((data) => {
        // Ensure data is an array
        setOrders(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Failed to fetch orders:', err);
        setOrders([]); // Set empty array on error
      });
  }, []);

  const handleEditClick = (id) => {
    navigate(`/orders/edit/${id}`);
  };

  const handleDeleteClick = (id) => {
    navigate(`/orders/delete/${id}`);
  };

  // `name` stays Macedonian: it is the key for visibleColumns. Only the
  // rendered header / dropdown label is translated.
  const allColumns = useMemo(() => [
    { name: 'ID', selector: (row) => row.id, sortable: true, width: '70px' },
    { name: 'Број на нарачка', selector: (row) => row.order_number, sortable: true },
    { name: 'Датум на нарачка', selector: (row) => row.order_date, sortable: true },
    { name: 'Купувач', selector: (row) => row.customer_name, sortable: true },
    { name: 'Статус', selector: (row) => row.status, sortable: true },
    { name: 'Вкупно ставки', selector: (row) => row.total_items, sortable: true, right: true },
    {
      name: 'Креирано',
      selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : ''),
      sortable: true,
    },
    {
      name: 'Акции',
      cell: (row) => (
        <div className="flex gap-2">
          <button onClick={() => handleEditClick(row.id)} title={t('Уреди')} className="text-indigo-600 hover:text-indigo-900">
            <Edit size={18} />
          </button>
          <button onClick={() => handleDeleteClick(row.id)} title={t('Избриши')} className="text-red-600 hover:text-red-900">
            <Trash size={18} />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '100px',
    },
  ], [lang]);

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map((col) => col.name).filter((name) => ['ID', 'Број на нарачка', 'Датум на нарачка', 'Статус', 'Акции'].includes(name))
  );

  const toggleColumn = (columnName) => {
    setVisibleColumns((prev) =>
      prev.includes(columnName) ? prev.filter((name) => name !== columnName) : [...prev, columnName]
    );
  };

  const columns = useMemo(
    () => allColumns
      .filter((col) => visibleColumns.includes(col.name))
      .map((col) => ({ ...col, name: t(col.name) })),
    [allColumns, visibleColumns, lang]
  );

  const filteredOrders = useMemo(() => {
    if (!Array.isArray(orders)) {
      return [];
    }
    return orders.filter((order) =>
      ['order_number', 'customer_name', 'status'].some(
        (field) => order[field] && order[field].toString().toLowerCase().includes(filter.toLowerCase())
      )
    );
  }, [orders, filter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  return (
    <div className="p-4">
      <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
        <input
          type="text"
          placeholder={t('Филтрирај по број, купувач или статус')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-72 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
        />

        <div className="flex gap-2 items-start">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="bg-gray-200 rounded border border-gray-300 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center gap-1 px-3 py-1.5 text-sm"
            >
              {t('Избери колони')} <span className="text-xs">&#9662;</span>
            </button>

            {showDropdown && (
              <div className="absolute left-0 w-48 max-h-60 overflow-auto bg-white border border-gray-300 rounded shadow-lg z-50 mt-1">
                {allColumns.map((col) => (
                  <label key={col.name} className="flex items-center px-2 py-1 cursor-pointer hover:bg-indigo-50">
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes(col.name)}
                      onChange={() => toggleColumn(col.name)}
                      className="mr-2"
                    />
                    <span className="text-gray-700">{t(col.name)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <button
            className="bg-indigo-700 hover:bg-indigo-800 text-white text-sm px-3 py-1.5 rounded font-medium"
            onClick={() => navigate('/orders/add')}
          >
            {t('Додади')}
          </button>

          <button
            className="bg-gray-700 hover:bg-gray-800 text-white text-sm px-3 py-1.5 rounded font-medium"
            onClick={() => window.location.reload()}
          >
            {t('Освежи')}
          </button>

          <CSVLink
            data={filteredOrders}
            filename="orders.csv"
            target="_blank"
            className="bg-green-400 hover:bg-green-500 text-gray-900 text-sm px-3 py-1.5 rounded font-medium no-underline"
          >
            {t('Извези')}
          </CSVLink>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredOrders}
        pagination
        highlightOnHover
        striped
        responsive
        persistTableHead
        noDataComponent={t('Нема пронајдени нарачки.')}
      />
    </div>
  );
}